import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { API_ENDPOINTS } from '../../config/api';
import './Profile.css';

const defaultBusinessComms = {
  newsletters: true,
  promotions: true,
  sales: true,
  marketing: true,
};

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { customer, isAuthenticated, logout, getAuthHeaders } = useCustomerAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [orders, setOrders] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [businessComms, setBusinessComms] = useState(defaultBusinessComms);
  const [savingComms, setSavingComms] = useState(false);
  const [commsMessage, setCommsMessage] = useState('');

  const currentBusinessKey = `${window.location.hostname}:${window.location.origin}`;
  const localCommsKey = customer?.id ? `frontend_business_comms:${customer.id}` : null;

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.customerProfile.orders, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to load orders');
      }

      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]);

  const loadConversations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.customerProfile.conversations, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to load conversations');
      }

      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]);

  const loadBusinessComms = useCallback(async () => {
    if (!localCommsKey) return;
    setCommsMessage('');

    try {
      const localRaw = localStorage.getItem(localCommsKey);
      const local = localRaw ? JSON.parse(localRaw) : {};
      const localForBusiness = local[currentBusinessKey] || defaultBusinessComms;
      setBusinessComms({ ...defaultBusinessComms, ...localForBusiness });
    } catch {
      setBusinessComms(defaultBusinessComms);
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.customerProfile.me.replace('/customer-profile/me', '/customers/me/preferences')}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) return;
      const data = await response.json();
      const remoteByBusiness =
        data?.preferences?.notificationPreferences?.businessByDomain || {};
      if (remoteByBusiness[currentBusinessKey]) {
        setBusinessComms((prev) => ({ ...prev, ...remoteByBusiness[currentBusinessKey] }));
      }
    } catch {
      // Remote sync may not be available for every token type; local fallback remains active.
    }
  }, [currentBusinessKey, getAuthHeaders, localCommsKey]);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'orders' || t === 'conversations' || t === 'profile') {
      setActiveTab(t);
    }
  }, [searchParams]);

  const goTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }

    // Load data based on active tab
    if (activeTab === 'orders') {
      loadOrders();
    } else if (activeTab === 'conversations') {
      loadConversations();
    } else if (activeTab === 'profile') {
      loadBusinessComms();
    }
  }, [isAuthenticated, activeTab, navigate, loadOrders, loadConversations, loadBusinessComms]);

  const handleLogout = () => {
    logout();
    navigate('/customer/login');
  };

  const saveBusinessComms = async () => {
    if (!localCommsKey) return;
    setSavingComms(true);
    setCommsMessage('');
    try {
      const localRaw = localStorage.getItem(localCommsKey);
      const local = localRaw ? JSON.parse(localRaw) : {};
      const mergedLocal = { ...local, [currentBusinessKey]: businessComms };
      localStorage.setItem(localCommsKey, JSON.stringify(mergedLocal));

      try {
        const getRes = await fetch(`${API_ENDPOINTS.customerProfile.me.replace('/customer-profile/me', '/customers/me/preferences')}`, {
          headers: getAuthHeaders(),
        });
        const existing = getRes.ok ? await getRes.json() : {};
        const existingPrefs = existing?.preferences?.notificationPreferences || {};
        const existingByBusiness = existingPrefs.businessByDomain || {};
        await fetch(`${API_ENDPOINTS.customerProfile.me.replace('/customer-profile/me', '/customers/me/preferences')}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            notificationPreferences: {
              ...existingPrefs,
              businessByDomain: {
                ...existingByBusiness,
                [currentBusinessKey]: businessComms,
              },
            },
          }),
        });
      } catch {
        // Local save still succeeds even if remote endpoint is unavailable.
      }

      setCommsMessage('Preferences saved for this business.');
    } catch {
      setCommsMessage('Could not save preferences right now.');
    } finally {
      setSavingComms(false);
    }
  };

  if (!customer) {
    return <div>Loading...</div>;
  }

  return (
    <div className="customer-profile-container">
      <div className="customer-profile-header">
        <h1>My Account</h1>
        <button onClick={handleLogout} className="logout-button">
          Log Out
        </button>
      </div>

      <div className="customer-profile-content">
        <div className="profile-sidebar">
          <div className="profile-info">
            <div className="profile-avatar">
              {customer.firstName?.[0]}{customer.lastName?.[0]}
            </div>
            <h2>{customer.firstName} {customer.lastName}</h2>
            <p>{customer.email}</p>
          </div>

          <nav className="profile-nav">
            <button
              className={activeTab === 'profile' ? 'active' : ''}
              onClick={() => goTab('profile')}
            >
              Profile
            </button>
            <button
              className={activeTab === 'orders' ? 'active' : ''}
              onClick={() => goTab('orders')}
            >
              Orders
            </button>
            <button
              className={activeTab === 'conversations' ? 'active' : ''}
              onClick={() => goTab('conversations')}
            >
              Conversations
            </button>
          </nav>
        </div>

        <div className="profile-main">
          {activeTab === 'profile' && (
            <div className="profile-tab">
              <h2>Profile Information</h2>
              <div className="profile-details">
                <div className="detail-row">
                  <label>Name</label>
                  <p>{customer.firstName} {customer.lastName}</p>
                </div>
                <div className="detail-row">
                  <label>Email</label>
                  <p>{customer.email}</p>
                </div>
                {customer.phone && (
                  <div className="detail-row">
                    <label>Phone</label>
                    <p>{customer.phone}</p>
                  </div>
                )}
                <div className="detail-row">
                  <label>Customer Type</label>
                  <p>{customer.customerType || 'Regular'}</p>
                </div>
                {customer.loyaltyPoints > 0 && (
                  <div className="detail-row">
                    <label>Loyalty Points</label>
                    <p>{customer.loyaltyPoints}</p>
                  </div>
                )}
                <div className="detail-row">
                  <label>Total Spent</label>
                  <p>${customer.totalSpent?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="detail-row">
                  <label>Orders</label>
                  <p>{customer.orderCount || 0}</p>
                </div>
              </div>

              <div style={{ marginTop: 24, padding: 16, borderRadius: 10, border: '1px solid #e5e7eb', background: '#fafafa' }}>
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>Business Communication Preferences</h3>
                <p style={{ marginTop: 0, color: '#666', fontSize: 13 }}>
                  Choose what you want to receive from this business.
                </p>
                <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                  {[
                    ['newsletters', 'Newsletters'],
                    ['promotions', 'Promotions'],
                    ['sales', 'Sales alerts'],
                    ['marketing', 'Marketing content'],
                  ].map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14 }}>
                      <span>{label}</span>
                      <input
                        type="checkbox"
                        checked={Boolean(businessComms[key])}
                        onChange={(e) => {
                          setBusinessComms((prev) => ({ ...prev, [key]: e.target.checked }));
                          setCommsMessage('');
                        }}
                      />
                    </label>
                  ))}
                </div>
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button
                    onClick={saveBusinessComms}
                    disabled={savingComms}
                    style={{
                      background: '#111827',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 14px',
                      cursor: savingComms ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                    }}
                  >
                    {savingComms ? 'Saving...' : 'Save Preferences'}
                  </button>
                  {commsMessage && <span style={{ fontSize: 13, color: '#166534' }}>{commsMessage}</span>}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="profile-tab">
              <h2>My Orders</h2>
              {isLoading ? (
                <p>Loading orders...</p>
              ) : orders.length === 0 ? (
                <p>No orders yet.</p>
              ) : (
                <div className="orders-list">
                  {orders.map((order) => (
                    <div key={order.id} className="order-card">
                      <div className="order-header">
                        <div>
                          <h3>Order #{order.orderNumber}</h3>
                          <p className="order-date">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="order-status">
                          <span className={`status-badge ${order.paymentStatus}`}>
                            {order.paymentStatus}
                          </span>
                          <span className={`status-badge ${order.fulfillmentStatus}`}>
                            {order.fulfillmentStatus}
                          </span>
                        </div>
                      </div>
                      <div className="order-items">
                        {order.items?.map((item) => (
                          <div key={item.id} className="order-item">
                            <div className="item-info">
                              <p className="item-name">{item.name}</p>
                              <p className="item-quantity">
                                {item.quantity} × ${Number(item.price || 0).toFixed(2)} each
                              </p>
                            </div>
                            <p className="item-price">${Number(item.total || 0).toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                      {(order.subtotal != null || order.tax != null || order.shipping != null) && (
                        <div
                          className="order-breakdown"
                          style={{
                            marginTop: '0.75rem',
                            paddingTop: '0.75rem',
                            borderTop: '1px solid #e5e7eb',
                            fontSize: '0.9rem',
                            color: '#4b5563',
                          }}
                        >
                          {order.subtotal != null && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Subtotal</span>
                              <span>${Number(order.subtotal).toFixed(2)}</span>
                            </div>
                          )}
                          {order.shipping != null && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Shipping</span>
                              <span>${Number(order.shipping).toFixed(2)}</span>
                            </div>
                          )}
                          {order.tax != null && (
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Tax (GST)</span>
                              <span>${Number(order.tax).toFixed(2)}</span>
                            </div>
                          )}
                          {Number(order.discount) > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#15803d' }}>
                              <span>Discount</span>
                              <span>-${Number(order.discount).toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {order.printfulOrderId && (
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.5rem' }}>
                          Printful: {order.printfulOrderId}
                        </p>
                      )}
                      <div className="order-total">
                        <strong>Total: ${Number(order.total || 0).toFixed(2)} AUD</strong>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'conversations' && (
            <div className="profile-tab">
              <h2>Conversations</h2>
              {isLoading ? (
                <p>Loading conversations...</p>
              ) : conversations.length === 0 ? (
                <p>No conversations yet. Start chatting with Rainbow! 🌈</p>
              ) : (
                <div className="conversations-list">
                  {conversations.map((conv) => (
                    <div key={conv.id} className="conversation-card">
                      <div className="conversation-header">
                        <h3>Conversation</h3>
                        <span className="conversation-date">
                          {new Date(conv.startedAt).toLocaleDateString()} - {new Date(conv.lastMessageAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="conversation-messages">
                        {conv.messages.map((msg) => (
                          <div key={msg.id} className={`message ${msg.role}`}>
                            <div className="message-role">
                              {msg.role === 'user' ? 'You' : 'Rainbow 🌈'}
                            </div>
                            <div className="message-content">
                              {msg.content.split('\n').map((line, i) => (
                                <React.Fragment key={i}>
                                  {line}
                                  {i < msg.content.split('\n').length - 1 && <br />}
                                </React.Fragment>
                              ))}
                            </div>
                            {msg.navigationUrl && (
                              <div className="message-navigation">
                                Navigated to: {msg.navigationUrl}
                              </div>
                            )}
                            <div className="message-time">
                              {new Date(msg.timestamp).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;

